/**
 * Save Photo Data Action
 *
 * Synchronous action that saves captured photo data to the appropriate context field
 * based on the current cardType and side from the state meta.
 */

import { assign } from "xstate";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Map cardType + side to context field name
 */
const getPhotoFieldName = (cardType: string, side: string): string | null => {
  const fieldMap: Record<string, Record<string, string>> = {
    license: {
      front: "licensePhotoFront",
      back: "licensePhotoBack",
    },
    primaryInsurance: {
      front: "primaryInsurancePhotoFront",
      back: "primaryInsurancePhotoBack",
    },
    secondaryInsurance: {
      front: "secondaryInsurancePhotoFront",
      back: "secondaryInsurancePhotoBack",
    },
    passport: {
      front: "passportPhoto",
    },
  };

  return fieldMap[cardType]?.[side] || null;
};

/**
 * Save Photo Data Action
 *
 * Saves the captured photo to the correct context field.
 * Expects event.data.photoData to contain the base64 image.
 * Uses cardType and side from the merged context (via state meta).
 *
 * Example usage in state machine:
 * ```
 * on: {
 *   CAPTURE: {
 *     target: "licenseFrontConfirm",
 *     actions: ["savePhotoData"],
 *   },
 * }
 * ```
 */
export const savePhotoData: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ event }: any) => {
    const { photoData, cardType, side } = event.data || {};

    if (!photoData) {
      return {};
    }

    if (!cardType || !side) {
      console.warn("[savePhotoData] Missing cardType or side in event.data", {
        cardType,
        side,
      });
      // Still save the photoData even if we can't determine the field
      return { photoData };
    }

    const fieldName = getPhotoFieldName(cardType, side);

    if (!fieldName) {
      console.warn("[savePhotoData] Unknown cardType/side combination", {
        cardType,
        side,
      });
      return { photoData };
    }

    return {
      [fieldName]: photoData,
      // Also store as current photoData for reference
      photoData,
    };
  }),
  metadata: {
    displayName: "savePhotoData",
    description: "Save captured photo to appropriate context field",
    category: "context",
  },
};
